export const ONBOARDING_MAX_SHOWS = 3;

export interface OnboardingStorageState {
  isCompleted: boolean;
  shownCount: number;
}

const createDefaultOnboardingStorageState = (): OnboardingStorageState => ({
  isCompleted: false,
  shownCount: 0
});

export const getOnboardingStorageKey = (userId: string | null | undefined): string | null => {
  const normalizedUserId = userId?.trim();

  return normalizedUserId ? `wl-onboarding:v2:${normalizedUserId}` : null;
};

const normalizeShownCount = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    return 0;
  }

  return Math.min(value, ONBOARDING_MAX_SHOWS);
};

const normalizeOnboardingStorageState = (value: unknown): OnboardingStorageState => {
  if (!value || typeof value !== 'object') {
    return createDefaultOnboardingStorageState();
  }

  const storageValue = value as Partial<OnboardingStorageState>;

  return {
    isCompleted: storageValue.isCompleted === true,
    shownCount: normalizeShownCount(storageValue.shownCount)
  };
};

export const getOnboardingStorageState = (userId: string | null | undefined): OnboardingStorageState => {
  const storageKey = getOnboardingStorageKey(userId);

  if (!storageKey) {
    return createDefaultOnboardingStorageState();
  }

  try {
    const rawValue = localStorage.getItem(storageKey);

    return rawValue
      ? normalizeOnboardingStorageState(JSON.parse(rawValue))
      : createDefaultOnboardingStorageState();
  } catch {
    return createDefaultOnboardingStorageState();
  }
};

const setOnboardingStorageState = (
  userId: string | null | undefined,
  state: OnboardingStorageState
): OnboardingStorageState => {
  const storageKey = getOnboardingStorageKey(userId);

  if (!storageKey) {
    return state;
  }

  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    return state;
  }

  return state;
};

export const completeOnboarding = (userId: string | null | undefined): OnboardingStorageState => {
  const state = getOnboardingStorageState(userId);

  return setOnboardingStorageState(userId, {
    ...state,
    isCompleted: true
  });
};

export const markOnboardingShown = (userId: string | null | undefined): OnboardingStorageState => {
  const state = getOnboardingStorageState(userId);

  return setOnboardingStorageState(userId, {
    ...state,
    shownCount: Math.min(state.shownCount + 1, ONBOARDING_MAX_SHOWS)
  });
};